import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { Heart, Mail, Linkedin, Github, ExternalLink, Code, Database, Server, Box, Zap, Palette, Globe } from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();





  const navigationLinks = [
    { name: "Home", to: "home" },
    { name: "About", to: "about" },
    { name: "Skills", to: "skills" },
    { name: "Experience", to: "experience" },
    { name: "Projects", to: "projects" },
    { name: "Contact", to: "contact" }
  ];

  const quickLinks = [
    { name: "Resume", href: "/TANVIRESUME_2026.pdf" },
    { name: "GitHub", href: "https://github.com/tanvisharma22" },
    { name: "LinkedIn", href: "https://www.linkedin.com/in/tanvi-sharma22" },
    { name: "Email", href: "https://mail.google.com/mail/?view=cm&fs=1&to=sharma.tanvi2297@gmail.com&su=Portfolio Inquiry&body=Hi Tanvi,%0D%0A%0D%0AI came across your portfolio and would like to discuss potential opportunities.%0D%0A%0D%0ABest regards," }
  ];

  return (
    <footer className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(120,119,198,0.3),transparent_50%)]"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[linear-gradient(45deg,transparent_25%,rgba(68,68,68,0.1)_25%,rgba(68,68,68,0.1)_75%,transparent_75%)] bg-[length:20px_20px]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-20">
          {/* Top Section - Brand & Navigation */}
          <div className="grid lg:grid-cols-2 gap-12 mb-16">
            {/* Brand Section */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                    <span className="text-white font-bold text-lg">T</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                      Tanvi Sharma
                    </h3>
                    <p className="text-gray-400 text-sm">Full Stack Developer</p>
                  </div>
                </div>

                <p className="text-gray-300 mb-6 leading-relaxed">
                  Passionate software developer crafting digital experiences with modern technologies.
                  Always learning, always building, always innovating.
                </p>

                {/* Social Links */}
                <div className="flex space-x-4">
                  {[
                    {
                      icon: Mail,
                      href: 'https://mail.google.com/mail/?view=cm&fs=1&to=sharma.tanvi2297@gmail.com&su=Portfolio Inquiry&body=Hi Tanvi,%0D%0A%0D%0AI came across your portfolio and would like to discuss potential opportunities.%0D%0A%0D%0ABest regards,',
                      label: 'Email',
                      color: 'hover:bg-red-500/20 hover:text-red-400'
                    },
                    {
                      icon: Linkedin,
                      href: 'https://www.linkedin.com/in/tanvi-sharma22',
                      label: 'LinkedIn',
                      color: 'hover:bg-blue-500/20 hover:text-blue-400'
                    },
                    {
                      icon: Github,
                      href: 'https://github.com/tanvisharma22',
                      label: 'GitHub',
                      color: 'hover:bg-gray-500/20 hover:text-gray-300'
                    }
                  ].map((social, index) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.1, y: -2 }}
                      className={`w-12 h-12 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl flex items-center justify-center text-gray-400 transition-all duration-300 ${social.color}`}
                    >
                      <social.icon className="w-5 h-5" />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Horizontal Navigation Links */}
            <div className="flex items-center justify-center lg:justify-end">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center lg:text-right"
              >
                <h4 className="text-lg font-semibold mb-6 text-white">
                  Navigation
                </h4>
                <div className="flex flex-wrap justify-center lg:justify-end gap-6">
                  {navigationLinks.map((link, index) => (
                    <motion.div
                      key={link.name}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.3 + index * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Link
                        to={link.to}
                        smooth={true}
                        duration={500}
                        className="text-gray-400 hover:text-primary-400 transition-all duration-300 cursor-pointer group flex items-center"
                      >
                        <span className="group-hover:translate-x-1 transition-transform duration-300 font-medium">
                          {link.name}
                        </span>
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Quick Links */}
                <div className="mt-8">
                  <h5 className="text-sm font-semibold mb-4 text-gray-300">
                    Quick Links
                  </h5>
                  <div className="flex flex-wrap justify-center lg:justify-end gap-4">
                    {quickLinks.map((link, index) => (
                      <motion.div
                        key={link.name}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.5 + index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <a
                          href={link.href}
                          target={link.href.startsWith('http') ? '_blank' : '_self'}
                          rel={link.href.startsWith('http') ? 'noopener noreferrer' : ''}
                          className="text-gray-400 hover:text-primary-400 transition-all duration-300 cursor-pointer group flex items-center text-sm"
                        >
                          <span className="group-hover:translate-x-1 transition-transform duration-300">
                            {link.name}
                          </span>
                          {link.href.startsWith('http') && (
                            <ExternalLink className="w-3 h-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          )}
                        </a>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Tech Stack Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 mb-12"
          >
            <div className="text-center">
              <motion.h3
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-2xl font-bold mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent"
              >
                Tech Stack
              </motion.h3>
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="text-gray-400 mb-8 text-lg"
              >
                Technologies I work with to bring ideas to life
              </motion.p>

              <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
                {[
                  { name: 'React', color: 'from-blue-500 to-blue-600', delay: 0.4, icon: Code },
                  { name: 'Node.js', color: 'from-green-500 to-green-600', delay: 0.5, icon: Server },
                  { name: 'TypeScript', color: 'from-blue-600 to-blue-700', delay: 0.6, icon: Code },
                  { name: 'MongoDB', color: 'from-green-600 to-green-700', delay: 0.7, icon: Database },
                  { name: 'MySQL', color: 'from-blue-700 to-blue-800', delay: 0.8, icon: Database },
                  { name: 'Docker', color: 'from-blue-400 to-blue-500', delay: 0.9, icon: Box },
                  { name: 'Redis', color: 'from-red-500 to-red-600', delay: 1.0, icon: Zap },
                  { name: 'Tailwind', color: 'from-cyan-500 to-cyan-600', delay: 1.1, icon: Palette },
                  { name: 'Vercel', color: 'from-black to-gray-800', delay: 1.2, icon: Globe },
                  { name: 'Railway', color: 'from-blue-500 to-blue-600', delay: 1.3, icon: Server },
                  { name: 'Netlify', color: 'from-green-500 to-green-600', delay: 1.4, icon: Globe }
                ].map((tech, index) => (
                  <motion.div
                    key={tech.name}
                    initial={{ opacity: 0, y: 20, scale: 0.8 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.5, delay: tech.delay }}
                    viewport={{ once: true }}
                    whileHover={{ scale: 1.05, y: -3 }}
                    className={`px-4 py-2 bg-gradient-to-r ${tech.color} rounded-full font-medium text-sm text-white shadow-lg hover:shadow-xl transition-all duration-300 cursor-default border border-white/10 flex items-center space-x-2`}
                  >
                    <tech.icon className="w-4 h-4" />
                    <span>{tech.name}</span>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 1.2 }}
                viewport={{ once: true }}
                className="mt-8 flex justify-center"
              >
                <div className="flex items-center space-x-3 text-gray-400 bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-full px-6 py-3">
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                    className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full"
                  />
                  <span className="text-sm font-medium">Always learning new technologies</span>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="border-t border-gray-700/50 py-8"
        >
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
            {/* Copyright */}
            <div className="text-gray-400 text-sm">
              © {currentYear} Tanvi Sharma. All rights reserved.
            </div>

            {/* Status Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-2"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400 font-medium">Available for Opportunities</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-2"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                <span className="text-blue-400 font-medium">Open to Remote Work</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
                className="flex items-center space-x-2 bg-purple-500/10 border border-purple-500/20 rounded-full px-4 py-2"
              >
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-ping"></div>
                <span className="text-purple-400 font-medium">Freelance Available</span>
              </motion.div>
            </div>

            {/* Made with Love */}
            <div className="flex items-center text-gray-400 text-sm bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-full px-4 py-2">
              Made with <Heart className="w-4 h-4 mx-1 text-red-500 animate-pulse" /> by Tanvi
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        viewport={{ once: true }}
        whileHover={{ scale: 1.1 }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white rounded-2xl shadow-2xl flex items-center justify-center transition-all duration-300 z-50 backdrop-blur-sm border border-white/10"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </motion.button>
    </footer>
  );
};

export default Footer; 